import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/store";
import type { Organization } from "../entities/Organization";
import * as organizationService from "../infrastructure/organization-service";
import { logout, loginWithCredentials, fetchUserProfile, refreshToken, restoreAuth } from "@/modules/auth/store/authSlice";

interface OrganizationState {
  items: Organization[];
  currentOrganizationId: string | null;
  isLoading: boolean;
  error: string | null;
}

// Persist only the current-org selection across refreshes. Everything else is
// rehydrated from the /auth/profile response by SessionInitializer.
const CURRENT_ORG_STORAGE_KEY = "currentOrganizationId";

function readPersistedOrgId(): string | null {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(CURRENT_ORG_STORAGE_KEY)
      : null;
  } catch {
    return null;
  }
}

function writePersistedOrgId(id: string | null): void {
  try {
    if (typeof window === "undefined") return;
    if (id) window.localStorage.setItem(CURRENT_ORG_STORAGE_KEY, id);
    else window.localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
  } catch {
    // ignore storage failures (private mode, quota, etc.)
  }
}

const initialState: OrganizationState = {
  items: [],
  currentOrganizationId: readPersistedOrgId(),
  isLoading: false,
  error: null,
};

export const fetchOrganizations = createAsyncThunk<
  Organization[],
  void,
  { rejectValue: string; state: RootState }
>("organizations/fetchAll", async (_, { rejectWithValue }) => {
  try {
    return await organizationService.getOrganizations();
  } catch (err) {
    const message =
      (err as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ?? "Failed to load organizations";
    return rejectWithValue(message);
  }
});

export const createOrganizationThunk = createAsyncThunk<
  Organization,
  organizationService.CreateOrganizationDto,
  { rejectValue: string }
>("organizations/create", async (dto, { rejectWithValue }) => {
  try {
    const result = await organizationService.createOrganization(dto);
    if (!result.isSuccess) {
      return rejectWithValue(result.errorCode ?? "Failed to create organization");
    }
    return result.value;
  } catch (err) {
    const message =
      (err as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ?? "Failed to create organization";
    return rejectWithValue(message);
  }
});

export const updateOrganizationThunk = createAsyncThunk<
  Organization,
  { id: string; dto: organizationService.UpdateOrganizationDto },
  { rejectValue: string }
>("organizations/update", async ({ id, dto }, { rejectWithValue }) => {
  try {
    return await organizationService.updateOrganization(id, dto);
  } catch (err) {
    const message =
      (err as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ?? "Failed to update organization";
    return rejectWithValue(message);
  }
});

export const deleteOrganizationThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("organizations/delete", async (id, { rejectWithValue }) => {
  try {
    await organizationService.deleteOrganization(id);
    return id;
  } catch (err) {
    const message =
      (err as Record<string, Record<string, Record<string, string>>>)?.response?.data?.message ?? "Failed to delete organization";
    return rejectWithValue(message);
  }
});

const organizationSlice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    setCurrentOrganizationId(state, action: PayloadAction<string | null>) {
      state.currentOrganizationId = action.payload;
      writePersistedOrgId(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.items = action.payload;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to load organizations";
      })

      .addCase(createOrganizationThunk.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.currentOrganizationId = action.payload.id;
        writePersistedOrgId(action.payload.id);
      })

      .addCase(updateOrganizationThunk.fulfilled, (state, action) => {
        state.items = state.items.map((org) =>
          org.id === action.payload.id ? { ...action.payload, userRole: action.payload.userRole || org.userRole } : org
        );
      })

      .addCase(deleteOrganizationThunk.fulfilled, (state, action) => {
        state.items = state.items.filter((org) => org.id !== action.payload);
        if (state.currentOrganizationId === action.payload) {
          const fallback = state.items[0]?.id ?? null;
          state.currentOrganizationId = fallback;
          writePersistedOrgId(fallback);
        }
      })

      .addCase(loginWithCredentials.fulfilled, (state, action) => {
        if (action.payload?.user?.currentOrganizationId) {
          state.currentOrganizationId = action.payload.user.currentOrganizationId;
          writePersistedOrgId(action.payload.user.currentOrganizationId);
        }
      })

      // Cold start (e.g. returning from an external payment redirect): restore
      // the org list and selection from the server profile so the app isn't
      // left without an active organisation.
      .addCase(restoreAuth, (state, action) => {
        const orgs = action.payload?.user?.organizations ?? [];
        if (orgs.length) {
          state.items = orgs;
        }
        // If we have a persisted selection but the user is no longer a member
        // of that org (deleted / membership revoked), fall back to the server's.
        if (
          state.currentOrganizationId &&
          orgs.length &&
          !orgs.some((o) => o.id === state.currentOrganizationId)
        ) {
          const fallback = action.payload?.user?.currentOrganizationId ?? null;
          state.currentOrganizationId = fallback;
          writePersistedOrgId(fallback);
        } else if (!state.currentOrganizationId && action.payload?.user?.currentOrganizationId) {
          state.currentOrganizationId = action.payload.user.currentOrganizationId;
          writePersistedOrgId(action.payload.user.currentOrganizationId);
        }
      })

      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        const orgs = action.payload?.organizations ?? [];
        if (orgs.length) {
          state.items = orgs;
        }
        if (
          state.currentOrganizationId &&
          orgs.length &&
          !orgs.some((o) => o.id === state.currentOrganizationId)
        ) {
          const fallback = action.payload?.currentOrganizationId ?? null;
          state.currentOrganizationId = fallback;
          writePersistedOrgId(fallback);
        } else if (!state.currentOrganizationId && action.payload?.currentOrganizationId) {
          state.currentOrganizationId = action.payload.currentOrganizationId;
          writePersistedOrgId(action.payload.currentOrganizationId);
        }
      })

      .addCase(refreshToken.fulfilled, (state, action) => {
        if (!state.currentOrganizationId && action.payload?.user?.currentOrganizationId) {
          state.currentOrganizationId = action.payload.user.currentOrganizationId;
          writePersistedOrgId(action.payload.user.currentOrganizationId);
        }
      })

      .addCase(logout, (state) => {
        state.items = [];
        state.currentOrganizationId = null;
        state.error = null;
        state.isLoading = false;
        writePersistedOrgId(null);
      });
  },
});

export const { setCurrentOrganizationId } = organizationSlice.actions;
export default organizationSlice.reducer;

export const selectOrganizationsState = (state: RootState) =>
  state.organizations;
export const selectOrganizations = (state: RootState) =>
  state.organizations.items;
export const selectOrganizationsLoading = (state: RootState) =>
  state.organizations.isLoading;
export const selectOrganizationsError = (state: RootState) =>
  state.organizations.error;
export const selectCurrentOrganizationId = (state: RootState) =>
  state.organizations.currentOrganizationId;
export const selectCurrentOrganization = (state: RootState) => {
  const { items, currentOrganizationId } = state.organizations;
  return items.find((o) => o.id === currentOrganizationId) ?? null;
};
